import {
  HttpHeaders,
  HttpClient,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, tap, catchError, of } from 'rxjs';
import { RuntimeConfigService } from '../../../services/runtime-config.service';
import {
  InlineImageListItem,
  Post,
  PostCommunicationSummary,
  PostLikes,
  PostCommunicationStats,
  PostEventRSVP,
  EventOccurrenceCapacity,
  PostFile,
  PostForm,
  PostFormAttachment,
  PostFormSubmission,
  PaymentNonBuyers,
  PostPaginationDTO,
  PostPayment,
  PostPaymentCreateDTO,
  PostPaymentCreateInPersonDTO,
  PostPaymentFinalizeInPersonDTO,
  PostPaymentRequest,
  PostSignUpList,
  PostSignUpListUser,
  PostUpcomingItemsDTO,
  PostVolunteerList,
  PostVolunteerListUser,
  PostConferenceList,
  PostConferenceSlotUser,
  PostTranslationSummaryDTO,
  PostFeedCalendarDTO,
  toDateKey
} from './models/post.model';
import { ConferenceSlotType } from './models/conference-slot-type.enum';
import { PostReadStatus } from './models/post-read-status.enum';
import { PostFilterOption } from './models/post-filter.enum';
import { PostFilterAddOn } from './models/post-filter-add-on.enum';
import { CheckoutResult } from './models/payment.model';
import { PostType } from './models/post-type.enum';
import { PerformanceMonitoringService } from '../../services/performance-monitoring.service';
import { sanitizeForSerialization } from '../shared/wire-sanitizer';
import { Student } from '../student/models/Student';
import { User } from '../../models/user.model';
import { ChatHouseholdGroup } from '../messages/models/ChatHouseholdGroup';

@Injectable({
  providedIn: 'root',
})
export class PostsService {
  private apiUrl: string;
  private paymentUrl: string;
  private calendarUrl: string;
  private phoneCallUrl: string;

  private postsFeedCache = new Map<string, BehaviorSubject<PostPaginationDTO | null>>();
  private upcomingItemsCache = new Map<string, BehaviorSubject<PostUpcomingItemsDTO | null>>();
  private feedCalendarCache = new Map<string, BehaviorSubject<PostFeedCalendarDTO | null>>();

  /**Keep track of the expanded posts, so their state is persisted on navigation*/
  private expandedPostIds = new Set<number>();

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  constructor(
    private http: HttpClient,
    private runtimeConfig: RuntimeConfigService,
    private performanceMonitoring: PerformanceMonitoringService
  ) {
    this.apiUrl = this.runtimeConfig.currentConfig.apiUrl + '/posts';
    this.paymentUrl = this.runtimeConfig.currentConfig.apiUrl + '/payment';
    this.calendarUrl = this.runtimeConfig.currentConfig.apiUrl + '/calendar';
    this.phoneCallUrl = this.runtimeConfig.currentConfig.apiUrl + '/PhoneCall';

    // Subscribe to config changes to update apiUrls
    this.runtimeConfig.config$.subscribe(config => {
      this.apiUrl = config.apiUrl + '/posts';
      this.paymentUrl = config.apiUrl + '/payment';
      this.calendarUrl = config.apiUrl + '/calendar';
      this.phoneCallUrl = config.apiUrl + '/PhoneCall';
    });
  }

  /**
   * Generate cache key for posts feed based on filter parameters
   * Only cache page 1 results
   */
  private getPostsFeedCacheKey(
    filterOption: PostFilterOption,
    schoolId?: number | null,
    groupId?: number | null,
    searchString?: string,
    postTagId?: number | null,
    addOnFilter?: PostFilterAddOn | null
  ): string {
    const group = groupId ?? 'all';
    const school = schoolId ?? 'all';
    const search = searchString || 'none';
    const tag = postTagId ?? 'none';
    const addOn = addOnFilter ?? 'none';
    return `${filterOption}_${school}_${group}_${search}_${tag}_${addOn}`;
  }

  /**
   * Clear all in-memory caches
   * Called during impersonation to ensure fresh data is loaded
   */
  clearCaches(): void {
    this.postsFeedCache.clear();
    this.upcomingItemsCache.clear();
    this.feedCalendarCache.clear();
  }

  /**Is the specified post expanded?*/
  public isPostExpanded(postId: number): boolean {
    return this.expandedPostIds.has(postId);
  }

  /**When a post is toggled, set its state here*/
  public setPostExpanded(postId: number, isExpanded: boolean): void {
    if (isExpanded) {
      this.expandedPostIds.add(postId);
    } else {
      this.expandedPostIds.delete(postId);
    }
  }

  /**When expand all is toggled off this is called*/
  public clearExpandedPosts(): void {
    this.expandedPostIds.clear();
  }

  //#region CREATE
  createNewPost(post: Post): Observable<Post> {
    let endpoint = post.PostTypeID == PostType.Critical ? 'CreateNewCriticalPost'
      : post.PostTypeID == PostType.QuickNotice ? 'CreateNewQuickNotice'
      : 'CreateNewPost';
    const sanitizedPost = sanitizeForSerialization(post);
    const updatedPost = {
      ...sanitizedPost,
      PostForms: sanitizedPost.PostForms
        ? sanitizedPost.PostForms.map((form) => ({
          ...form,
          FormData: JSON.stringify(form.FormData),
        }))
        : [],
    };
    return this.http.post<Post>(`${this.apiUrl}/${endpoint}`, updatedPost).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(`${this.apiUrl}/${endpoint}`)),
      map((returnedPost) => ({
        ...returnedPost,
        PostForms: returnedPost.PostForms?.map((form) => ({
          ...form,
          FormData: (() => {
            try {
              if (typeof form.FormData === 'string') {
                return JSON.parse(form.FormData);
              } else if (form.FormData && typeof form.FormData === 'object') {
                return form.FormData;
              } else {
                return [];
              }
            } catch (parseError) {
              console.error('Error parsing FormData in createNewPost:', parseError, 'FormData:', form.FormData);
              return [];
            }
          })(),
        })) || [],
      }))
    );
  }

  /**
   * Create a calendar "quick event" in a single call: the server saves the post + event,
   * publishes it, marks it event-only, and sends NO notifications.
   */
  createQuickEvent(post: Post): Observable<Post> {
    const url = `${this.apiUrl}/CreateQuickEvent`;
    return this.http.post<Post>(url, post).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url))
    );
  }

  createPostFile(formData: FormData) {
    var url = `${this.apiUrl}/CreatePostFile`;
    return this.http.post(url, formData).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url))
    );
  }

  createPostMediaFile(postFile: PostFile) {
    var url = `${this.apiUrl}/CreatePostMediaFile`;
    return this.http.post<number>(url, postFile).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url))
    );
  }

  createPostNotifications(postId: number): Observable<void> {
    var url = `${this.apiUrl}/CreatePostNotifications/${postId}`;
    return this.http.post<void>(url, {}).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url))
    );
  }

  createFormSubmission(submission: PostFormSubmission): Observable<number> {
    var url = `${this.apiUrl}/CreateSubmission/`;
    return this.http.post<number>(url, submission).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url))
    );
  }

  createAddFormSubmissionFiles(files: File[], submissionId: number): Observable<void> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    var url = `${this.apiUrl}/UploadSubmissionFiles/${submissionId}`;
    return this.http.post<void>(
      url,
      formData
    ).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url))
    );
  }

  // #endregion CREATE

  // #region READ
  getPostsForUserAsync(
    pageNumber: number,
    pageSize: number,
    filterOption: PostFilterOption,
    schoolId: number,
    groupId?: number | null,
    searchString: string = '',
    postTagId?: number | null,
    addOnFilter?: PostFilterAddOn | null
  ): Observable<PostPaginationDTO> {
    let params: HttpParams = new HttpParams();
    params = params.set('PageNumber', pageNumber.toString());
    params = params.set('PageSize', pageSize.toString());
    params = params.set('FilterOption', filterOption.toString());
    params = params.set('schoolId', schoolId.toString());

    if (groupId) params = params.set('GroupId', groupId.toString());
    if (searchString) params = params.set('SearchString', searchString);
    if (postTagId) params = params.set('postTagId', postTagId.toString());
    if (addOnFilter != null) params = params.set('AddOnFilter', addOnFilter.toString());

    const url = `${this.apiUrl}/GetPosts`;
    return this.http.get<PostPaginationDTO>(url, { params }).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
      map((response) => ({
        ...response,
        Results: response.Results.map((post) => ({
          ...post,
          PostForms: post.PostForms?.map((form) => ({
            ...form,
            FormData: (() => {
              try {
                if (typeof form.FormData === 'string') {
                  return JSON.parse(form.FormData);
                } else if (form.FormData && typeof form.FormData === 'object') {
                  return form.FormData;
                } else {
                  return [];
                }
              } catch (parseError) {
                console.error('Error parsing FormData in posts.service:', parseError, 'FormData:', form.FormData);
                return [];
              }
            })(),
          })) || [],
        })),
      }))
    );
  }

  /**
   * Get posts with in-memory cache-first strategy
   * Returns cached data immediately if available within the session, then fetches fresh data in background
   * Only caches page 1 results in memory (lost on page refresh)
   */
  getPostsForUserWithCache(
    pageNumber: number,
    pageSize: number,
    filterOption: PostFilterOption,
    schoolId: number,
    groupId?: number | null,
    searchString: string = '',
    postTagId?: number | null,
    addOnFilter?: PostFilterAddOn | null
  ): Observable<PostPaginationDTO | null> {
    // Only cache page 1
    if (pageNumber !== 1) {
      return this.getPostsForUserAsync(pageNumber, pageSize, filterOption, schoolId, groupId, searchString, postTagId, addOnFilter);
    }

    const cacheKey = this.getPostsFeedCacheKey(filterOption, schoolId, groupId, searchString, postTagId, addOnFilter);

    if (!this.postsFeedCache.has(cacheKey)) {
      this.postsFeedCache.set(cacheKey, new BehaviorSubject<PostPaginationDTO | null>(null));

      this.fetchAndCachePostsFeed(cacheKey, pageNumber, pageSize, filterOption, schoolId, groupId, searchString, postTagId, addOnFilter);
    } else {
      // Cache subject exists, fetch fresh data
      this.fetchAndCachePostsFeed(cacheKey, pageNumber, pageSize, filterOption, schoolId, groupId, searchString, postTagId, addOnFilter);
    }

    return this.postsFeedCache.get(cacheKey)!.asObservable();
  }

  private fetchAndCachePostsFeed(
    cacheKey: string,
    pageNumber: number,
    pageSize: number,
    filterOption: PostFilterOption,
    schoolId: number,
    groupId?: number | null,
    searchString?: string,
    postTagId?: number | null,
    addOnFilter?: PostFilterAddOn | null
  ): void {
    this.getPostsForUserAsync(pageNumber, pageSize, filterOption, schoolId, groupId, searchString, postTagId, addOnFilter)
      .pipe(
        tap(response => {
          // Update in-memory cache only (no persistent storage)
          const cacheSubject = this.postsFeedCache.get(cacheKey);
          if (cacheSubject) {
            cacheSubject.next(response);
          }
        }),
        catchError(error => {
          console.error('[PostsCache] Error fetching posts feed:', error);

          // Emit null to notify subscribers that request completed (with error)
          const cacheSubject = this.postsFeedCache.get(cacheKey);
          if (cacheSubject) {
            cacheSubject.error(error);
          }

          // Remove the cache entry so next attempt creates a fresh BehaviorSubject
          this.postsFeedCache.delete(cacheKey);

          return of(null);
        })
      )
      .subscribe();
  }

  getPostsUpcomingItemsAsync(): Observable<PostUpcomingItemsDTO> {
    const url = `${this.apiUrl}/GetPostsUpcomingItems`;
    return this.http.get<PostUpcomingItemsDTO>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url))
    );
  }

  /**
   * Get upcoming items with in-memory cache-first strategy
   * Cached in memory only (lost on page refresh)
   */
  getPostsUpcomingItemsWithCache(): Observable<PostUpcomingItemsDTO | null> {
    const cacheKey = 'all';

    if (!this.upcomingItemsCache.has(cacheKey)) {
      this.upcomingItemsCache.set(cacheKey, new BehaviorSubject<PostUpcomingItemsDTO | null>(null));

      this.fetchAndCacheUpcomingItems(cacheKey);
    } else {
      this.fetchAndCacheUpcomingItems(cacheKey);
    }

    return this.upcomingItemsCache.get(cacheKey)!.asObservable();
  }

  /**
   * Re-fetch upcoming items into the existing cache subject so the feed sidebar
   * updates its submitted status after an add-on submission changes.
   */
  refreshUpcomingItems(): void {
    const cacheKey = 'all';
    if (this.upcomingItemsCache.has(cacheKey)) {
      this.fetchAndCacheUpcomingItems(cacheKey);
    }
  }

  private fetchAndCacheUpcomingItems(cacheKey: string): void {
    this.getPostsUpcomingItemsAsync()
      .pipe(
        tap(response => {
          // Update in-memory cache only (no persistent storage)
          const cacheSubject = this.upcomingItemsCache.get(cacheKey);
          if (cacheSubject) {
            cacheSubject.next(response);
          }
        }),
        catchError(error => {
          console.error('[UpcomingItemsCache] Error fetching upcoming items:', error);

          // Emit null to notify subscribers that request completed (with error)
          const cacheSubject = this.upcomingItemsCache.get(cacheKey);
          if (cacheSubject) {
            cacheSubject.next(null);
          }

          // Remove the cache entry so next attempt creates a fresh BehaviorSubject
          this.upcomingItemsCache.delete(cacheKey);

          return of(null);
        })
      )
      .subscribe();
  }

  getPostFeedCalendar(selectedDate: string): Observable<PostFeedCalendarDTO> {
    const url = `${this.apiUrl}/GetPostFeedCalendar`;
    return this.http.get<PostFeedCalendarDTO>(url, { params: new HttpParams().set('selectedDate', selectedDate) }).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url))
    );
  }

  /**
   * Only today is cached, so returning to the feed paints without a spinner. Other days are a
   * plain fetch - they are never the view you come back to, so caching them buys nothing and
   * would grow this map without bound.
   */
  getPostFeedCalendarWithCache(selectedDate: string): Observable<PostFeedCalendarDTO | null> {
    if (selectedDate !== toDateKey(new Date())) {
      return this.getPostFeedCalendar(selectedDate);
    }

    const cacheKey = `today:${selectedDate}`;
    if (!this.feedCalendarCache.has(cacheKey)) {
      this.feedCalendarCache.set(cacheKey, new BehaviorSubject<PostFeedCalendarDTO | null>(null));
    }

    this.fetchAndCacheFeedCalendar(cacheKey, selectedDate);
    return this.feedCalendarCache.get(cacheKey)!.asObservable();
  }

  private fetchAndCacheFeedCalendar(cacheKey: string, selectedDate: string): void {
    this.getPostFeedCalendar(selectedDate)
      .pipe(
        tap(response => this.feedCalendarCache.get(cacheKey)?.next(response)),
        catchError(error => {
          console.error('[FeedCalendarCache] Error fetching feed calendar:', error);
          this.feedCalendarCache.get(cacheKey)?.error(error);
          this.feedCalendarCache.delete(cacheKey);
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * Playback URL for a Critical Alert's recorded phone message. The endpoint is anonymous by
   * design (Azure Communication Services fetches the audio from it during the call) and the GUID
   * is the access token, so an <audio> tag can point straight at it with no auth header.
   */
  getCriticalAlertAudioUrl(audioGuid: string): string {
    return `${this.phoneCallUrl}/audio/${audioGuid}`;
  }

  /**
   * @param includeDesign Only the designer/edit screens need the DesignJson projectData. Viewers
   * leave this false so the server never ships the nvarchar(max) blob — `Post.IsDesigned` tells
   * the detail view which render branch to take, which is all it ever used DesignJson for.
   */
  getPostByIdAsync(
    postId: number,
    communicationId?: number,
    useSourceLanguage: boolean = false,
    includeDesign: boolean = false,
  ): Observable<Post> {
    let params = new HttpParams();

    if (communicationId) {
      params = params.set('communication', communicationId.toString());
    }

    if (includeDesign) {
      params = params.set('includeDesign', 'true');
    }

    var url = `${this.apiUrl}/GetPostById/${postId}/${useSourceLanguage}`;
    return this.http
      .get<Post>(url, { params: params })
      .pipe(
        tap(() => this.performanceMonitoring.captureMetrics(url)),
        map((post) => this.parsePostFormData(post))
      );
  }

  /** The edit screen's read: same payload, but 403s when the user can't edit the post. */
  getPostForEditAsync(postId: number): Observable<Post> {
    var url = `${this.apiUrl}/GetPostForEdit/${postId}`;
    return this.http
      .get<Post>(url)
      .pipe(
        tap(() => this.performanceMonitoring.captureMetrics(url)),
        map((post) => this.parsePostFormData(post))
      );
  }

  private parsePostFormData(post: Post): Post {
    return {
      ...post,
      PostForms: post.PostForms.map((form) => ({
        ...form,
        FormData: (() => {
          try {
            // Check if FormData is already an object or needs parsing
            if (typeof form.FormData === 'string') {
              return JSON.parse(form.FormData);
            } else if (form.FormData && typeof form.FormData === 'object') {
              return form.FormData;
            } else {
              return [];
            }
          } catch (parseError) {
            console.error('Error parsing FormData in posts.service:', parseError, 'FormData:', form.FormData);
            return [];
          }
        })(),
      })),
    };
  }

  getPostFilesInfo(postId: number): Observable<PostFile[]> {
    var url = `${this.apiUrl}/GetPostFilesInfo/${postId}`;
    return this.http.get<PostFile[]>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
    )
  }

  getPostFileById(postFileId: number): Observable<PostFile> {
    var url = `${this.apiUrl}/GetPostFileById/${postFileId}`;
    return this.http.get<PostFile>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
    )
  }

  getPostEventRSVPs(postId: number, occurrenceDate?: string | null): Observable<PostEventRSVP[]> {
    var url = `${this.apiUrl}/GetRSVPsForEvent/${postId}`;
    if (occurrenceDate) {
      url += `?occurrenceDate=${occurrenceDate}`;
    }
    return this.http.get<PostEventRSVP[]>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
    );
  }

  getMyEventRSVPs(postId: number): Observable<PostEventRSVP[]> {
    const url = `${this.apiUrl}/GetMyRSVPsForEvent/${postId}`;
    return this.http.get<PostEventRSVP[]>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
    );
  }

  getEventCapacity(postId: number): Observable<EventOccurrenceCapacity[]> {
    const url = `${this.apiUrl}/GetEventCapacity/${postId}`;
    return this.http.get<EventOccurrenceCapacity[]>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
    );
  }

  getFormSubmissionSummary(formId: number): Observable<PostForm> {
    var url = `${this.apiUrl}/GetFormSubmissionSummary/${formId}`;
    return this.http.get<PostForm>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
      map((form) => ({
        ...form,
        FormData: (() => {
          try {
            if (typeof form.FormData === 'string') {
              return JSON.parse(form.FormData);
            } else if (form.FormData && typeof form.FormData === 'object') {
              return form.FormData;
            } else {
              return [];
            }
          } catch (parseError) {
            console.error('Error parsing FormData in getFormSubmissionSummary:', parseError, 'FormData:', form.FormData);
            return [];
          }
        })(),
        // Strip file bytes from attachments to reduce data transfer
        // File bytes will be loaded on-demand when downloading
        Submissions: form.Submissions?.map(submission => ({
          ...submission,
          Attachments: submission.Attachments?.map(attachment => ({
            ...attachment,
            FileBytes: undefined
          }))
        }))
      }))
    );
  }

  getFormAttachmentById(postId: number, attachmentId: number): Observable<PostFormAttachment> {
    var url = `${this.apiUrl}/GetFormAttachmentById/${postId}/${attachmentId}`;
    return this.http.get<PostFormAttachment>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url))
    );
  }

  getPayments(paymentRequestId: number): Observable<PostPayment[]> {
    var url = `${this.paymentUrl}/GetPayments/${paymentRequestId}`;
    return this.http.get<PostPayment[]>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url))
    );
  }

  getSelfPayments(paymentRequestId: number): Observable<PostPayment[]> {
    var url = `${this.paymentUrl}/GetSelfPayments/${paymentRequestId}`;
    return this.http.get<PostPayment[]>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url))
    );
  }

  getFullVolunteerList(id: number): Observable<PostVolunteerList> {
    var url = `${this.apiUrl}/GetFullVolunteerList/${id}`;
    return this.http.get<PostVolunteerList>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
    );
  }

  getFullConferenceList(id: number): Observable<PostConferenceList> {
    var url = `${this.apiUrl}/GetFullConferenceList/${id}`;
    return this.http.get<PostConferenceList>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
    );
  }

  getParentEligibleStudents(postId: number): Observable<Student[]> {
    var url = `${this.apiUrl}/GetParentEligibleStudents/${postId}`;
    return this.http.get<Student[]>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
    );
  }

  getStudentsInPostSections(postId: number): Observable<Student[]> {
    var url = `${this.apiUrl}/GetStudentsInPostSections/${postId}`;
    return this.http.get<Student[]>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
    );
  }

  getStudentsForPostRecipients(postId: number): Observable<Student[]> {
    var url = `${this.apiUrl}/GetStudentsForPostRecipients/${postId}`;
    return this.http.get<Student[]>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
    );
  }

  /** Recipients who have not submitted the given form (household-aware; includes staff recipients). */
  getFormNonResponderGuardians(formId: number): Observable<User[]> {
    var url = `${this.apiUrl}/GetFormNonResponderGuardians/${formId}`;
    return this.http.get<User[]>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
    );
  }

  /** Recipients who have not booked a slot in the given conference list. */
  getConferenceNonResponders(conferenceListId: number): Observable<User[]> {
    var url = `${this.apiUrl}/GetConferenceNonResponders/${conferenceListId}`;
    return this.http.get<User[]>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
    );
  }

  /** Households among the given recipients for a post's compose dialog, with students
   *  narrowed by the post's context (auto-class sections / creator's roster). */
  getPostRecipientHouseholds(postId: number, userIds: number[]): Observable<ChatHouseholdGroup[]> {
    var url = `${this.apiUrl}/GetPostRecipientHouseholds/${postId}`;
    return this.http.post<ChatHouseholdGroup[]>(url, userIds).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
    );
  }

  /** Recipients with no (non-refunded) payment anywhere in their household for the given
   *  request, plus the count of households that haven't purchased. */
  getPaymentNonBuyers(paymentRequestId: number): Observable<PaymentNonBuyers> {
    var url = `${this.apiUrl}/GetPaymentNonBuyers/${paymentRequestId}`;
    return this.http.get<PaymentNonBuyers>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
    );
  }

  getFullSignupList(id: number): Observable<PostSignUpList> {
    var url = `${this.apiUrl}/GetFullSignupList/${id}`;
    return this.http.get<PostSignUpList>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
    );
  }

  getFullPaymentRequest(id: number): Observable<PostPaymentRequest> {
    var url = `${this.apiUrl}/GetFullPaymentRequest/${id}`;
    return this.http.get<PostPaymentRequest>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
    );
  }
  // #endregion READ

  // #region UPDATE
 updatePost(post: Post): Observable<Post> {
  const sanitizedPost = sanitizeForSerialization(post);
  const updatedPost = {
    ...sanitizedPost,
    // Preserve null: the designer sends PostForms null to mean "don't touch
    // add-ons" and the server skips null collections. Coercing to [] here
    // would instead tell the server to delete every existing form.
    PostForms: sanitizedPost.PostForms
      ? sanitizedPost.PostForms.map((form) => ({
        ...form,
        FormData: JSON.stringify(form.FormData),
      }))
      : sanitizedPost.PostForms,
    };

    var url = `${this.apiUrl}/UpdatePost`;
    return this.http.post<Post>(url, updatedPost).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url)),
      map((returnedPost) => ({
        ...returnedPost,
        PostForms: returnedPost.PostForms?.map((form) => ({
          ...form,
          FormData: (() => {
            try {
              if (typeof form.FormData === 'string') {
                return JSON.parse(form.FormData);
              } else if (form.FormData && typeof form.FormData === 'object') {
                return form.FormData;
              } else {
                return [];
              }
            } catch (parseError) {
              console.error('Error parsing FormData in updatePost:', parseError, 'FormData:', form.FormData);
              return [];
            }
          })(),
        })) || [],
      }))
    );
  }

  /**
   * Submit/publish a post. If the post is already published, this triggers a resubmit.
   * @param postId The post ID to submit
   * @param sendNotifications Whether to send notifications to recipients
   * @param editMessage Optional message explaining what changed (only used on resubmit)
   */
  updateSubmitPost(postId: number, sendNotifications: boolean, editMessage?: string, newRecipientsOnly: boolean = false): Observable<boolean> {
    let url = `${this.apiUrl}/SubmitPost/${postId}/${sendNotifications}`;
    const params: string[] = [];
    if (editMessage) {
      params.push(`editMessage=${encodeURIComponent(editMessage)}`);
    }
    if (newRecipientsOnly) {
      params.push('newRecipientsOnly=true');
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    return this.http.get<boolean>(url).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url))
    );
  }

  updatePostFileTitle(postFileId: number, title: string) {
    var url = `${this.apiUrl}/UpdatePostFileTitle/${postFileId}`;
    return this.http.post(url, { Title: title }).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url))
    );
  }

  // #endregion UPDATE

  // #region DELETE
  deletePostFile(postFileId: number) {
    var url = `${this.apiUrl}/DeletePostFile/${postFileId}`;
    return this.http.post(url, {}).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url))
    );
  }

  deletePost(postId: number) {
    var url = `${this.apiUrl}/DeletePost/${postId}`;  
    return this.http.post(url, {}).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url))
    );
  }

  deleteFormSubmission(submissionId: number): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/DeleteFormSubmission/${submissionId}`,
      null
    );
  }
  // #endregion DELETE



  // UNIMPLEMENTED

  // get  
 


  // CREATE



 createAddSignupUser(
    signUpListItemId: number,
    isSelf: boolean,
    userName?: string,
    notes?: string,
    quantity: number = 1,
    email?: string,
    phone?: string
  ): Observable<PostSignUpListUser> {
    var signUpUser = new PostSignUpListUser();
    signUpUser.SignUpListItemId = signUpListItemId;
    signUpUser.IsSelf = isSelf;
    signUpUser.UserName = userName;
    signUpUser.Notes = notes;
    signUpUser.Quantity = quantity;
    signUpUser.Email = email;
    signUpUser.Phone = phone;

    return this.http.post<PostSignUpListUser>(`${this.apiUrl}/SignUpAddUser/`, signUpUser);
  }
    public createAddVolunteerUser(
    volunteerListTaskId: number,
    isSelf: boolean,
    userName?: string,
    notes?: string,
    email?: string,
    phone?: string
  ): Observable<PostVolunteerListUser> {
    var volunteerAddUser = new PostVolunteerListUser();
    volunteerAddUser.VolunteerListTaskId = volunteerListTaskId;
    volunteerAddUser.IsSelf = isSelf;
    volunteerAddUser.UserName = userName;
    volunteerAddUser.Notes = notes;
    volunteerAddUser.Email = email;
    volunteerAddUser.Phone = phone;
    return this.http.post<PostVolunteerListUser>(
      `${this.apiUrl}/VolunteerAddUser/`, volunteerAddUser);
  }

  public createAddConferenceUser(
    conferenceSlotId: number,
    isSelf: boolean,
    userName?: string,
    notes?: string,
    studentId?: number,
    preferredSlotType?: ConferenceSlotType
  ): Observable<PostConferenceSlotUser> {
    var conferenceAddUser = new PostConferenceSlotUser();
    conferenceAddUser.ConferenceSlotId = conferenceSlotId;
    conferenceAddUser.IsSelf = isSelf;
    conferenceAddUser.UserName = userName;
    conferenceAddUser.Notes = notes;
    conferenceAddUser.StudentId = studentId;
    conferenceAddUser.PreferredSlotType = preferredSlotType;
    return this.http.post<PostConferenceSlotUser>(
      `${this.apiUrl}/ConferenceAddUser/`, conferenceAddUser);
  }

 /** Creates a pending offline payment; returns the new payment's id. */
 public createInPersonPayment(data: PostPaymentCreateInPersonDTO): Observable<number> {
    return this.http.post<number>(`${this.paymentUrl}/CreateInPersonPayment/`, data);
  }

  public createPayment(data: PostPaymentCreateDTO): Observable<CheckoutResult> {
    return this.http.post<CheckoutResult>(`${this.paymentUrl}/CreatePayment/`, data);
  }

  // UPDATE

 
  updatePostMemberReadStatus(
    postId: number,
    readStatus: PostReadStatus
  ): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.apiUrl}/UpdatePostMemberReadStatus/${postId}`,
      { readStatus: readStatus }
    );
  }

  /** Likes or unlikes the post for the current user; returns the new like count. */
  setPostLike(postId: number, liked: boolean): Observable<number> {
    return this.http.post<number>(
      `${this.apiUrl}/SetPostLike/${postId}`,
      { liked: liked }
    );
  }

  public getPostLikes(postId: number, pageNumber: number, pageSize: number): Observable<PostLikes> {
    const params = new HttpParams()
      .set('PageNumber', pageNumber)
      .set('PageSize', pageSize);
    return this.http.get<PostLikes>(`${this.apiUrl}/GetPostLikes/${postId}`, { params: params });
  }

  /**
   * Archive every post currently visible for the given feed filters in a single request.
   * The user is resolved server-side from the auth context. Returns the number of posts archived.
   */
  archiveAllPosts(
    filterOption: PostFilterOption,
    schoolId: number,
    groupId?: number | null,
    searchString?: string,
    postTagId?: number | null,
    addOnFilter?: PostFilterAddOn | null
  ): Observable<number> {
    const body = {
      FilterOption: filterOption,
      GroupId: groupId ?? null,
      SearchString: searchString && searchString.trim() !== '' ? searchString : null,
      SchoolId: schoolId,
      PostTagId: postTagId ?? null,
      AddOnFilter: addOnFilter ?? null
    };
    const url = `${this.apiUrl}/ArchiveAllPosts`;
    return this.http.post<number>(url, body).pipe(
      tap(() => this.performanceMonitoring.captureMetrics(url))
    );
  }


  

  public updatePaymentRefund(paymentId: number): Observable<void> {
    return this.http.post<void>(`${this.paymentUrl}/RefundPayment/${paymentId}`, null);
  }

  public finalizeInPersonPayment(paymentId: number, data: PostPaymentFinalizeInPersonDTO): Observable<void> {
    return this.http.post<void>(`${this.paymentUrl}/FinalizeInPersonPayment/${paymentId}`, data);
  }

 
 
  // DELETE



public deleteSignUpUser(signUpListUserId: number): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/SignUpRemoveUser/${signUpListUserId}`,
      null
    );
  }
public deleteVolunteerUser(volunteerListUserId: number): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/VolunteerRemoveUser/${volunteerListUserId}`,
      null
    );
  }

  public deleteConferenceUser(conferenceSlotUserId: number): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/ConferenceRemoveUser/${conferenceSlotUserId}`,
      null
    );
  }

  public updateConferenceUserAttendance(
    postId: number,
    conferenceSlotUserId: number,
    attendanceStatus: number,
    attendanceMethod: number,
    attendanceNotes?: string
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.apiUrl}/UpdateConferenceUserAttendance`,
      {
        postId,
        conferenceSlotUserId,
        attendanceStatus,
        attendanceMethod,
        attendanceNotes
      }
    );
  }

  //#region POST TAGS
  getTags(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/GetPostTags`);
  }

  createTag(postTag: any): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}/CreatePostTag`, postTag);
  }

  updateTag(postTag: any): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/UpdatePostTag`, postTag);
  }

  deleteTag(tagId: number): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/DeletePostTag/${tagId}`, {});
  }
  //#endregion

  //#region POST ON BEHALF OF
  getPostOnBehalfOfs(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/GetPostOnBehalfOfs`);
  }

  createPostOnBehalfOf(postOnBehalfOf: any): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}/CreatePostOnBehalfOf`, postOnBehalfOf);
  }

  updatePostOnBehalfOf(postOnBehalfOf: any): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/UpdatePostOnBehalfOf`, postOnBehalfOf);
  }

  deletePostOnBehalfOf(postOnBehalfOfId: number): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/DeletePostOnBehalfOf/${postOnBehalfOfId}`, {});
  }

  /**A null pageSize returns every row*/
  public getPostCommunicationSummary(postId: number, pageNumber: number, pageSize: number | null, sortField: string, sortDirection: 'asc' | 'desc', search?: string, showEventReminders: boolean = false, showRemovedRecipients: boolean = false): Observable<PostCommunicationSummary> {
    let params = new HttpParams()
      .set('PageNumber', pageNumber)
      .set('SortField', sortField)
      .set('SortDirection', sortDirection)
      .set('ShowEventReminders', showEventReminders)
      .set('ShowRemovedRecipients', showRemovedRecipients);
    if (pageSize != null) {
      params = params.set('PageSize', pageSize);
    }
    if (search) {
      params = params.set('Search', search);
    }
    return this.http.get<PostCommunicationSummary>(`${this.apiUrl}/GetPostCommunicationSummary/${postId}`, { params: params });
  }

  /**Gets the page-independent communication counts for a post (called once when the modal opens)*/
  public getPostCommunicationStats(postId: number): Observable<PostCommunicationStats> {
    return this.http.get<PostCommunicationStats>(`${this.apiUrl}/GetPostCommunicationStats/${postId}`);
  }

  /**Gets the view counts for a list of post Ids*/
  public getPostViewCounts(postIds: number[]): Observable<{[postId: number]: number}> {
    return this.http.post<{[postId: number]: number}>(`${this.apiUrl}/GetPostViewCounts`, postIds);
  }

  public getPostTranslationSummary(postId: number): Observable<PostTranslationSummaryDTO[]> {
    return this.http.get<PostTranslationSummaryDTO[]>(`${this.apiUrl}/GetPostTranslationSummary/${postId}`);
  }

  public getEndDateOfCurrentAcademicYear(schoolId: number): Observable<Date> {
    return this.http.get<Date>(`${this.calendarUrl}/GetEndDateForCurrentAcademicYear/${schoolId}`).pipe(
      map(date => new Date(date as unknown as string))
    );
  }
  //#endregion

  //#region INLINE IMAGES
  getMyInlineImages(): Observable<InlineImageListItem[]> {
    return this.http.get<InlineImageListItem[]>(`${this.apiUrl}/GetMyInlineImages`);
  }

  uploadInlineImage(postId: number, file: File): Observable<InlineImageListItem> {
    return new Observable<InlineImageListItem>(subscriber => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        this.http.post<InlineImageListItem>(`${this.apiUrl}/UploadInlineImage`, {
          PostId: postId,
          FileBytes: base64,
          ContentType: file.type,
          FileName: file.name,
        }).subscribe({
          next: res => { subscriber.next(res); subscriber.complete(); },
          error: err => subscriber.error(err),
        });
      };
      reader.onerror = err => subscriber.error(err);
      reader.readAsDataURL(file);
    });
  }
  //#endregion
}
